"use client";

import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { useNativeLocation } from "@/hooks/use-native-location";
import { ShiftSummaryDialog } from "@/components/shifts/shift-summary-dialog";
import { useDashboard } from "@/contexts/dashboard-context";
import { formatDuration } from "@/lib/geo-utils";
import { Play, Square, Loader2, MapPin, Pause, Clock } from "lucide-react";
import { formatTimeWithPreference } from "@/lib/format";
import { useTimeFormat } from "@/lib/time-format-context";
import { IssuePhotoFab } from "@/components/blocks/issue-photo-fab";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

export function ShiftControl() {
  const timeFormat = useTimeFormat();
  const [isLoading, setIsLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completedShiftId, setCompletedShiftId] = useState<Id<"shifts"> | null>(null);

  // Use consolidated dashboard state
  const { activeShift, activeBlock } = useDashboard();

  const startShiftMutation = useMutation(api.shifts.startShift);
  const endShiftMutation = useMutation(api.shifts.endShift);
  const pauseShift = useMutation(api.shifts.pauseShift);
  const resumeShift = useMutation(api.shifts.resumeShift);

  const {
    isReady,
    isTracking,
    totalMiles: localTotalMiles,
    isLowPowerMode,
    startTracking,
    stopTracking,
    resumeTracking,
    getCurrentPosition,
    getLocationPoints,
    clearSession,
    error: locationError,
  } = useNativeLocation();

  const batchSyncLocationPoints = useMutation(api.shifts.batchSyncLocationPoints);

  // Auto-resume local tracking when app loads with existing active shift
  // This handles page refresh, app restart, or returning to the app
  useEffect(() => {
    if (!isReady || isTracking || !activeShift) return;
    if (activeShift.status === "completed") return;

    // There's an active shift but local tracking isn't running - resume it
    const doResume = async () => {
      console.log("Auto-resuming tracking for existing shift:", activeShift._id);
      const result = await resumeTracking(activeShift._id);
      if (result.resumed) {
        console.log("Tracking resumed with", result.restoredMiles.toFixed(2), "restored miles");
      } else {
        console.error("Failed to resume tracking");
      }
    };

    doResume();
  }, [isReady, isTracking, activeShift, resumeTracking]);

  // Update elapsed time every second when shift is active
  useEffect(() => {
    if (!activeShift || activeShift.status === "completed") {
      setElapsedTime(0);
      return;
    }

    const updateElapsed = () => {
      const now = Date.now();
      const pauseTime = activeShift.pauseDuration || 0;
      const elapsed = now - activeShift.startedAt - pauseTime;
      setElapsedTime(elapsed);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeShift]);

  // Check if current active shift is a commute (not a block shift)
  const isCommuteActive = activeShift &&
    activeShift.status !== "completed" &&
    !activeShift.blockId &&
    activeShift.type !== "block";

  const handleStartShift = useCallback(async () => {
    if (!isReady || !activeBlock) {
      console.error("Location not ready or no active block");
      return;
    }

    setIsLoading(true);
    try {
      // If there's an active commute, stop it first (seamless transition)
      if (isCommuteActive && activeShift) {
        // Get location points from native storage
        const nativePoints = await getLocationPoints(activeShift._id);

        // Stop tracking and get accumulated data
        const trackingData = await stopTracking();
        const milesToSave = trackingData?.totalMiles ?? 0;

        // Sync location points to Convex
        if (nativePoints.length > 0) {
          await batchSyncLocationPoints({
            shiftId: activeShift._id,
            points: nativePoints,
          });
        }

        // End the commute shift
        await endShiftMutation({
          shiftId: activeShift._id,
          totalMiles: milesToSave,
        });

        // Clear native session data
        await clearSession(activeShift._id);

        toast({
          title: "Commute saved",
          description: `${milesToSave.toFixed(1)} mi tracked`,
        });
      }

      // Now start block tracking
      const position = await getCurrentPosition();
      if (!position) {
        throw new Error("Could not get current position");
      }

      // Create shift in Convex
      const shiftId = await startShiftMutation({
        lat: position.lat,
        lng: position.lng,
        blockId: activeBlock._id,
      });

      // Start LOCAL tracking (no server calls during tracking)
      const trackingStarted = await startTracking({
        shiftId: shiftId,
        initialLat: position.lat,
        initialLng: position.lng,
      });

      if (!trackingStarted) {
        // Tracking failed to start - likely permission issue
        // End the shift we just created since we can't track it
        await endShiftMutation({ shiftId });
        toast({
          title: "Location permission required",
          description:
            'Enable "Always" location access in Settings to track mileage in the background.',
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      toast({
        title: "Failed to start shift",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [isReady, activeBlock, activeShift, isCommuteActive, getCurrentPosition, startShiftMutation, startTracking, getLocationPoints, stopTracking, batchSyncLocationPoints, endShiftMutation, clearSession]);

  const handleEndShift = useCallback(async () => {
    if (!activeShift) return;

    setIsLoading(true);
    try {
      // Get location points from native storage BEFORE stopping tracking
      const nativePoints = await getLocationPoints(activeShift._id);

      // Stop tracking and get accumulated data from native storage
      const trackingData = await stopTracking();

      const milesToSave = trackingData?.totalMiles ?? 0;

      // Sync location points to Convex for map visualization
      if (nativePoints.length > 0) {
        await batchSyncLocationPoints({
          shiftId: activeShift._id,
          points: nativePoints,
        });
      }

      await endShiftMutation({
        shiftId: activeShift._id,
        totalMiles: milesToSave,
      });

      // Clear native session data after successful sync
      await clearSession(activeShift._id);

      setCompletedShiftId(activeShift._id);
    } catch (error) {
      toast({
        title: "Failed to end shift",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [activeShift, getLocationPoints, stopTracking, batchSyncLocationPoints, endShiftMutation, clearSession]);

  const handlePauseShift = useCallback(async () => {
    if (!activeShift) return;

    setIsLoading(true);
    try {
      await pauseShift({ shiftId: activeShift._id });
    } catch (error) {
      toast({
        title: "Failed to pause shift",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [activeShift, pauseShift]);

  const handleResumeShift = useCallback(async () => {
    if (!activeShift) return;

    setIsLoading(true);
    try {
      await resumeShift({ shiftId: activeShift._id });
    } catch (error) {
      toast({
        title: "Failed to resume shift",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [activeShift, resumeShift]);

  const isShiftActive = activeShift && activeShift.status !== "completed";
  // Only consider block shifts (not commutes) for this component
  const isBlockShiftActive = isShiftActive && (activeShift?.blockId || activeShift?.type === "block");

  // Display local tracking miles (persisted to localStorage)
  const displayMiles = localTotalMiles;

  // Determine tracking status for glow effect
  const getGlowClass = () => {
    if (!isBlockShiftActive) return "";
    if (locationError) return "tracking-glow-red";
    if (activeShift?.status === "paused" || isLowPowerMode) return "tracking-glow-yellow";
    return "tracking-glow-green";
  };

  // Hide entirely if no active block shift AND no active block ready to track
  if (!isBlockShiftActive && !activeBlock) {
    return null;
  }

  return (
    <div className={`rounded-lg border bg-card p-4 space-y-3 transition-all duration-300 ${getGlowClass()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">Route Tracking</span>
        </div>
        <div className="flex items-center gap-2">
          {isBlockShiftActive && activeShift?.blockId && (
            <IssuePhotoFab blockId={activeShift.blockId} />
          )}
          {isBlockShiftActive && (
            <div className="text-sm text-muted-foreground">
              {formatDuration(elapsedTime)}
            </div>
          )}
        </div>
      </div>

      {locationError && (
        <p className="text-sm text-destructive">{locationError}</p>
      )}

      {isBlockShiftActive ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div
              className={`h-2 w-2 rounded-full ${
                activeShift.status === "paused"
                  ? "bg-yellow-500"
                  : "bg-green-500 animate-pulse"
              }`}
            />
            <span className={`text-sm font-bold ${activeShift.status !== "paused" ? "shimmer-text text-green-500" : ""}`}>
              {activeShift.status === "paused" ? "Paused" : "Tracking"}
            </span>
            {isTracking && isLowPowerMode && (
              <span className="text-xs text-muted-foreground">(power saving)</span>
            )}
          </div>

          <div className="text-2xl font-bold tabular-nums">
            {displayMiles.toFixed(1)} mi
          </div>

          <div className="flex gap-2">
            {activeShift.status === "paused" ? (
              <Button
                onClick={handleResumeShift}
                disabled={isLoading}
                variant="outline"
                className="flex-1"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Resume
              </Button>
            ) : (
              <Button
                onClick={handlePauseShift}
                disabled={isLoading}
                variant="outline"
                className="flex-1"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Pause className="h-4 w-4 mr-2" />
                )}
                Pause
              </Button>
            )}
            <Button
              onClick={handleEndShift}
              disabled={isLoading}
              variant="destructive"
              className="flex-1"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Square className="h-4 w-4 mr-2" />
              )}
              End Route
            </Button>
          </div>
        </div>
      ) : activeBlock ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{formatTimeWithPreference(activeBlock.shiftStart, timeFormat)} - {formatTimeWithPreference(activeBlock.shiftEnd, timeFormat)} block ready</span>
          </div>
          {isCommuteActive && (
            <p className="text-xs text-muted-foreground">
              Your commute will be saved when you start
            </p>
          )}
          <Button
            onClick={handleStartShift}
            disabled={isLoading || !isReady}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Start Route
          </Button>
        </div>
      ) : null}

      {!isReady && !locationError && (
        <p className="text-sm text-muted-foreground text-center">
          Initializing location...
        </p>
      )}

      <ShiftSummaryDialog
        shiftId={completedShiftId}
        open={completedShiftId !== null}
        onOpenChange={(open) => {
          if (!open) setCompletedShiftId(null);
        }}
      />
    </div>
  );
}
